'use client';

import { c as _c } from "react/compiler-runtime";
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RepeatWrapping, SRGBColorSpace, type Texture, TextureLoader, Vector2 } from 'three';
import { uniform } from 'three/tsl';
import { AddEquation, CustomBlending, MeshBasicNodeMaterial, OneFactor, SrcAlphaFactor, ZeroFactor } from 'three/webgpu';
import type { EngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { ENGINE_POWER_BURST_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-burst/config';
import { createPowerBurstNodes } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-burst/powerBurstShader';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';
import logger from '@/lib/logger';
const FIRE_TEXTURE_SRC = 'https://assets.isuzu-tis.com/photoreal_fire_168ec176da.png';
interface PowerBurstProps {
  anchorRef?: MutableRef<EngineAnchor>;
  visibilityRef?: MutableRef<number>;
  burstKey?: number;
  warmUp?: boolean;
  isAnimating?: boolean;
}
export const PowerBurst = t0 => {
  const $ = _c(28);
  const {
    visibilityRef,
    burstKey: t1,
    anchorRef,
    warmUp: t2,
    isAnimating: t3
  } = t0;
  const burstKey = t1 === undefined ? 0 : t1;
  const warmUp = t2 === undefined ? false : t2;
  const isAnimating = t3 === undefined ? true : t3;
  const viewport = useThree(_temp);
  const size = useThree(_temp2);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      time: uniform(10),
      origin: uniform(new Vector2(0.5, 0.5)),
      engineSize: uniform(0.6),
      aspect: uniform(1),
      opacity: uniform(0)
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const uniforms = t4;
  const writeRef = useRef(uniforms);
  let t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      writeRef.current = uniforms;
    };
    t6 = [uniforms];
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  useEffect(t5, t6);
  const [fire, setFire] = useState(null);
  let t7;
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      let disposed = false;
      const loaded = new TextureLoader().load(FIRE_TEXTURE_SRC, ready => {
        if (disposed) {
          return;
        }
        ready.colorSpace = SRGBColorSpace;
        ready.wrapS = ready.wrapT = RepeatWrapping;
        ready.needsUpdate = true;
        setFire(ready);
      }, undefined, _temp3);
      return () => {
        disposed = true;
        loaded.dispose();
      };
    };
    t8 = [];
    $[3] = t7;
    $[4] = t8;
  } else {
    t7 = $[3];
    t8 = $[4];
  }
  useEffect(t7, t8);
  let t9;
  bb0: {
    if (!fire) {
      t9 = null;
      break bb0;
    }
    let next;
    if ($[5] !== fire) {
      next = new MeshBasicNodeMaterial();
      next.colorNode = createPowerBurstNodes(uniforms.time, uniforms.aspect, uniforms.origin, uniforms.engineSize, fire);
      next.opacityNode = uniforms.opacity;
      next.transparent = true;
      next.blending = CustomBlending;
      next.blendEquation = AddEquation;
      next.blendSrc = SrcAlphaFactor;
      next.blendDst = OneFactor;
      next.blendEquationAlpha = AddEquation;
      next.blendSrcAlpha = ZeroFactor;
      next.blendDstAlpha = OneFactor;
      next.depthWrite = false;
      next.depthTest = false;
      next.toneMapped = false;
      $[5] = fire;
      $[6] = next;
    } else {
      next = $[6];
    }
    t9 = next;
  }
  const material = t9;
  const materialRef = useRef(material);
  let t10;
  let t11;
  if ($[7] !== burstKey) {
    t10 = () => {
      if (burstKey > 0) {
        writeRef.current.time.value = 0;
      }
    };
    t11 = [burstKey];
    $[7] = burstKey;
    $[8] = t10;
    $[9] = t11;
  } else {
    t10 = $[8];
    t11 = $[9];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[10] !== material) {
    t12 = () => {
      materialRef.current = material;
      return () => material?.dispose();
    };
    t13 = [material];
    $[10] = material;
    $[11] = t12;
    $[12] = t13;
  } else {
    t12 = $[11];
    t13 = $[12];
  }
  useEffect(t12, t13);
  let t14;
  if ($[13] !== anchorRef?.value || $[14] !== burstKey || $[15] !== isAnimating || $[16] !== size || $[17] !== viewport.height || $[18] !== viewport.width || $[19] !== visibilityRef?.value || $[20] !== warmUp) {
    t14 = (_state, delta) => {
      const visibility = visibilityRef?.value ?? 1;
      const values = writeRef.current;
      if (!isAnimating) {
        values.time.value = 10;
      } else {
        if (visibility > 0.001 && burstKey > 0 && values.time.value < ENGINE_POWER_BURST_CONFIG.duration) {
          values.time.value = values.time.value + Math.min(delta, 0.05) * ENGINE_POWER_BURST_CONFIG.playbackRate;
        }
      }
      const anchor = anchorRef?.value;
      if (anchor?.isMeasured && size.width > 0 && size.height > 0) {
        values.origin.value.set(anchor.centerX / size.width, 1 - anchor.centerY / size.height);
        values.engineSize.value = anchor.size / size.height;
      }
      values.aspect.value = viewport.width / Math.max(viewport.height, 0.001);
      values.opacity.value = visibility * ENGINE_POWER_BURST_CONFIG.opacity;
      if (materialRef.current) {
        materialRef.current.visible = warmUp || visibility > 0.001 && values.time.value < ENGINE_POWER_BURST_CONFIG.duration;
      }
    };
    $[13] = anchorRef?.value;
    $[14] = burstKey;
    $[15] = isAnimating;
    $[16] = size;
    $[17] = viewport.height;
    $[18] = viewport.width;
    $[19] = visibilityRef?.value;
    $[20] = warmUp;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  useFrame(t14);
  if (!material) {
    return null;
  }
  let t15;
  if ($[22] !== viewport.height || $[23] !== viewport.width) {
    t15 = <planeGeometry args={[viewport.width, viewport.height]} />;
    $[22] = viewport.height;
    $[23] = viewport.width;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== material || $[26] !== t15) {
    t16 = <mesh material={material} renderOrder={-1} frustumCulled={false}>{t15}</mesh>;
    $[25] = material;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  return t16;
};
function _temp(state) {
  return state.viewport;
}
function _temp2(state_0) {
  return state_0.size;
}
function _temp3() {
  logger.warn("[webgpu] power burst texture failed", {
    src: FIRE_TEXTURE_SRC
  });
}